import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Index from "./pages/Index";

// Code-split secondary routes to reduce initial JS payload (PageSpeed: unused JS)
const Services = lazy(() => import("./pages/Services"));
const TechnologyAdvisory = lazy(() => import("./pages/services/TechnologyAdvisory"));
const AIEnablement = lazy(() => import("./pages/services/AIEnablement"));
const InvestorReadiness = lazy(() => import("./pages/services/InvestorReadiness"));
const Prototyping = lazy(() => import("./pages/services/Prototyping"));
const DiscoverySessions = lazy(() => import("./pages/services/DiscoverySessions"));
const AIAdvisory = lazy(() => import("./pages/services/AIAdvisory"));
const InvestorTechReadiness = lazy(() => import("./pages/services/InvestorTechReadiness"));
const ProductDiscoveryMVP = lazy(() => import("./pages/services/ProductDiscoveryMVP"));
const ExecutiveDashboards = lazy(() => import("./pages/services/ExecutiveDashboards"));
const HowWeWork = lazy(() => import("./pages/HowWeWork"));
const HowWeThink = lazy(() => import("./pages/HowWeThink"));
const AITechReadinessAudit = lazy(() => import("./pages/AITechReadinessAudit"));
const IndustriesIndex = lazy(() => import("./pages/industries/IndustriesIndex"));
const IndustryDetail = lazy(() => import("./pages/industries/IndustryDetail"));
const WhoWeHelp = lazy(() => import("./pages/WhoWeHelp"));
const About = lazy(() => import("./pages/About"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Insights = lazy(() => import("./pages/Insights"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const ContactThankYou = lazy(() => import("./pages/ContactThankYou"));
const BookCall = lazy(() => import("./pages/BookCall"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// ScrollToTop component to handle page navigation scroll reset
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/technology-advisory" element={<TechnologyAdvisory />} />
            <Route path="/services/outsourced-cto" element={<Navigate to="/services/technology-advisory" replace />} />
            <Route path="/insights/what-outsourced-cto-does" element={<Navigate to="/insights/what-a-strategic-technology-advisor-does" replace />} />
            <Route path="/services/ai-enablement" element={<AIEnablement />} />
            <Route path="/services/investor-readiness" element={<InvestorReadiness />} />
            <Route path="/services/prototyping" element={<Prototyping />} />
            <Route path="/services/discovery-sessions" element={<DiscoverySessions />} />
            <Route path="/services/ai-advisory" element={<AIAdvisory />} />
            <Route path="/services/investor-tech-readiness" element={<InvestorTechReadiness />} />
            <Route path="/services/product-discovery-mvp" element={<ProductDiscoveryMVP />} />
            <Route path="/services/executive-dashboards" element={<ExecutiveDashboards />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/how-we-think" element={<HowWeThink />} />
            <Route path="/ai-tech-readiness-audit" element={<AITechReadinessAudit />} />
            <Route path="/industries" element={<IndustriesIndex />} />
            <Route path="/industries/:slug" element={<IndustryDetail />} />
            <Route path="/who-we-help" element={<WhoWeHelp />} />
            <Route path="/about" element={<About />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<BlogPost />} />
            <Route path="/strategic-engagements" element={<CaseStudies />} />
            <Route path="/strategic-engagements/:slug" element={<CaseStudyDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contact/thank-you" element={<ContactThankYou />} />
            <Route path="/book-call" element={<BookCall />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

